import { createPinia } from 'pinia';
import SecureLS from 'secure-ls';
import { useAuthStore } from './auth';
import { usePdfStore } from './pdf';

const ls = new SecureLS({ isCompression: false });

function createSecureLSPersistPlugin() {
	return ({ store, options }) => {
		if (!options.persist) return;
		const config = typeof options.persist === 'object' ? options.persist : {};
		const key = config.key || store.$id;

		// 1. Synchronously rehydrate store state on initialization
		try {
			const raw = ls.get(key);
			if (raw) {
				const data = (typeof raw === 'object' && raw.auth) ? raw.auth : (typeof raw === 'string' ? JSON.parse(raw) : raw);
				if (data && typeof data === 'object' && Object.keys(data).length > 0) {
					store.$patch(data);
				}
			}
		} catch (e) {
			console.error('[SecureLS Hydration Error]:', e);
		}

		// 2. Synchronously persist store state on every change
		store.$subscribe((mutation, state) => {
			try {
				const plainState = JSON.parse(JSON.stringify(state));
				ls.set(key, plainState);
			} catch (e) {
				console.error('[SecureLS Persist Error]:', e);
			}
		}, { detached: true, flush: 'sync' });
	};
}

export const pinia = createPinia();
pinia.use(createSecureLSPersistPlugin());

export function createPiniaVuexBridge(piniaInstance) {
	return {
		get state() {
			return {
				get auth() {
					return useAuthStore(piniaInstance);
				},
				get pdf() {
					return usePdfStore(piniaInstance);
				},
			};
		},
		getters: new Proxy({}, {
			get(target, prop) {
				if (typeof prop !== 'string') return undefined;
				const authStore = useAuthStore(piniaInstance);
				const pdfStore = usePdfStore(piniaInstance);
				if (prop.startsWith('auth/')) {
					const getterName = prop.replace('auth/', '');
					return authStore[getterName];
				}
				if (prop.startsWith('pdf/')) {
					const getterName = prop.replace('pdf/', '');
					return pdfStore[getterName];
				}
				if (prop in authStore) {
					return authStore[prop];
				}
				if (prop in pdfStore) {
					return pdfStore[prop];
				}
				return undefined;
			},
		}),
		commit(type, payload) {
			const authStore = useAuthStore(piniaInstance);
			const pdfStore = usePdfStore(piniaInstance);
			const parts = type.split('/');
			const module = parts.length > 1 ? parts[0] : '';
			const action = parts.length > 1 ? parts[1] : parts[0];

			if (module === 'auth' || !module) {
				if (typeof authStore[action] === 'function') {
					return authStore[action](payload);
				}
				if (action in authStore) {
					authStore[action] = payload;
					return;
				}
			}
			if (module === 'pdf' || !module) {
				if (typeof pdfStore[action] === 'function') {
					return pdfStore[action](payload);
				}
				if (action in pdfStore) {
					pdfStore[action] = payload;
					return;
				}
			}
		},
		dispatch(type, payload) {
			const authStore = useAuthStore(piniaInstance);
			const pdfStore = usePdfStore(piniaInstance);
			const parts = type.split('/');
			const module = parts.length > 1 ? parts[0] : '';
			const action = parts.length > 1 ? parts[1] : parts[0];

			if (module === 'auth' || !module) {
				if (typeof authStore[action] === 'function') {
					return authStore[action](payload);
				}
			}
			if (module === 'pdf' || !module) {
				if (typeof pdfStore[action] === 'function') {
					return pdfStore[action](payload);
				}
			}
		},
	};
}

export const store = createPiniaVuexBridge(pinia);
export { useAuthStore } from './auth';
export { usePdfStore } from './pdf';
export default store;
